import DownloadIcon from "@mui/icons-material/Download";
import { Box, IconButton } from "@mui/material";
import { useState } from "react";
import { Outlet, useOutletContext } from "react-router-dom";
import CustomDataGridV2 from "../../../../../../../components/CustomDataGridV2";
import Flex from "../../../../../../../components/Flex";
import PdfViewDrawer from "../../../../../../../components/PdfViewDrawer";
import { ScrollArea } from "../../../../../../../components/ScrollBar";
import { SimpleAccordion } from "../../../../../../../components/SimpleAccordion";
import Text from "../../../../../../../components/Text";
import useSelectedPatient from "../../../../../../../hooks/local-storage/use-selected-patient";
import useDisclosure from "../../../../../../../hooks/use-disclosure";
import { usePatientAssessmentList } from "../../../../../../../queries/patient-chart/assessment/use-assessment-get";
import { usePatientAssessmentPdfDownload } from "../../../../../../../queries/patient-chart/assessment/use-assessment-pdf-download";
import { formatDate } from "../../../../../../../utils/date-time-utility";
import {
  DEF_PAGINATION,
  applyDefaultColumnSettings,
  getXtenantId,
} from "../../../../../../../utils/utils";

const ScreeningAssessment = () => {
  const { setIsNewScreeningView } = useOutletContext<{
    setIsNewScreeningView: (value: boolean) => void;
  }>();

  const isVisitNotePage = window.location.pathname.includes("/visit-note");

  return (
    <Flex width={"100%"} height={"100%"}>
      {isVisitNotePage ? (
        <ScreeningTable />
      ) : (
        <Outlet context={{ setIsNewScreeningView }} />
      )}
    </Flex>
  );
};

export default ScreeningAssessment;

export const ScreeningTable = () => {
  const [curItemExpand, setCurItemExpand] = useState<string>();
  const [selectedPatient] = useSelectedPatient();

  const { data } = usePatientAssessmentList({
    pagination: DEF_PAGINATION,
    gridSorting: [],
    patientId: selectedPatient?.uuid,
    xTENANTID: getXtenantId(),
  });

  const { mutateAsync: downloadAssessmentPdf } =
    usePatientAssessmentPdfDownload();

  const onDownloadClick = (row: any) => {
    downloadAssessmentPdf({
      assessmentPDFRequest: {
        patientId: selectedPatient?.uuid,
        screeningAt: row.screeningAt,
        screeningBy: row.screeningBy,
        assessments: row.responses,
      },
      xTENANTID: getXtenantId(),
    }).then((res) => {
      const blob = new Blob([res?.data], { type: "application/pdf" });

      const fileUrl = URL.createObjectURL(blob);

      printViewDisclosure.open(fileUrl);
    });
  };

  const COLUMNS = [
    "Sr. No",
    "Name",
    "Assigned By",
    "Responsible Person",
    // "Score",
    "Assigned At",
    "Action",
  ];

  const innerColumns = applyDefaultColumnSettings<any>([
    {
      field: "",
      headerName: "",
      width: 25,
      renderCell: () => "",
    },
    {
      field: "srNo",
      headerName: "",
      width: 70,
      renderCell: (params) =>
        String.fromCharCode(
          params.api.getRowIndexRelativeToVisibleRows(params.id) + 97
        ),
    },
    {
      field: "AssessmentName",
      headerName: "",
      flex: 0.4,
      width: 200,
      renderCell: ({ value }) => value || "-",
    },
    // {
    //   field: "screeningBy",
    //   headerName: "Assigned By",
    //   flex: 0.3,
    //   width: 150,
    //   renderCell: ({ value }) => value || "-",
    // },
    // {
    //   field: "responsiblePerson",
    //   headerName: "Responsible Person",
    //   flex: 0.3,
    //   width: 150,
    //   renderCell: ({ value }) => value || "-",
    // },
    {
      field: "total_score",
      headerName: "",
      flex: 0.2,
      width: 100,
      renderCell: ({ value }) => `Score: ${value || "-"}`,
    },
    // {
    //   field: "assignedAt",
    //   headerName: "Assigned At",
    //   flex: 0.3,
    //   width: 180,
    //   renderCell: ({ value }) =>
    //     value ? formatDateTime(value,  ) : "-",
    // },
    // {
    //   field: "updateDate",
    //   headerName: "Updated At",
    //   flex: 0.3,
    //   width: 180,
    //   renderCell: ({ value }) => NonNullableDate(value),
    // },
    // {
    //   field: "status",
    //   headerName: "Status",
    //   flex: 0.3,
    //   width: 120,
    //   renderCell: ({ value }) => (
    //     <Box
    //       sx={{
    //         backgroundColor:
    //           value === "Completed"
    //             ? "#E8F5E8"
    //             : value === "In Progress"
    //             ? "#FFF3E0"
    //             : "#FCE4EC",
    //         color:
    //           value === "Completed"
    //             ? "#2E7D32"
    //             : value === "In Progress"
    //             ? "#F57C00"
    //             : "#C2185B",
    //         padding: "4px 8px",
    //         borderRadius: "4px",
    //         fontSize: "12px",
    //         fontWeight: 500,
    //       }}
    //     >
    //       {value || "Pending"}
    //     </Box>
    //   ),
    // },
    // {
    //   field: "Action",
    //   headerName: "",
    //   width: 120,
    //   hideSortIcons: true,
    //   align: "center",
    //   headerAlign: "center",
    //   renderCell: ({ row }) => (
    //     <IconButton onClick={() => onDownloadClick(row)}>
    //       <DownloadIcon />
    //     </IconButton>
    //   ),
    // },
    {
      field: "",
      headerName: "",
      width: 23,
      renderCell: () => "",
    },
  ]);

  interface AccordionHeadProps {
    row: any;
    index: number;
  }

  const AccordionHead = ({ row, index }: AccordionHeadProps) => {
    return (
      <Flex width={"100%"} height={"inherit"}>
        <Flex flex={0.1} minWidth={"50px"}>
          <Text isTabletext>{index + 1 || "-"}</Text>
        </Flex>
        <Flex flex={2.2}>
          <Text isTabletext minWidth={"200px"} fontWeight="medium">
            {row?.assessmentName || "-"}
          </Text>
        </Flex>
        <Flex flex={1}>
          <Text isTabletext>{row?.screeningBy || "-"}</Text>
        </Flex>
        <Flex flex={1}>
          <Text isTabletext>{row?.responsiblePerson || "-"}</Text>
        </Flex>
        {/* <Flex flex={0.9}>
          <Text>{row?.score || "-"}</Text>
        </Flex> */}
        <Flex flex={1.2}>
          <Text isTabletext>
            {row?.screeningAt ? formatDate(row.screeningAt) : "-"}
          </Text>
        </Flex>
        <Flex flex={0.4}>
          <IconButton onClick={() => onDownloadClick(row)}>
            <DownloadIcon />
          </IconButton>
        </Flex>
      </Flex>
    );
  };

  const widthMapper: Record<number, string> = {
    0: "50px",
    1: "35%",
    3: "17%",
    4: "19%",
  };

  const printViewDisclosure = useDisclosure();

  return (
    <Box width={"100%"} height={"100%"}>
      <PdfViewDrawer
        w="65vw"
        file={printViewDisclosure?.data}
        disclosureHost={printViewDisclosure}
        title="Screening Assessment Report"
      />
      <Flex
        width={"100%"}
        bgcolor={"#EFF6FF"}
        height={"45px"}
        sx={{ placeItems: "center" }}
        mb={1}
      >
        {COLUMNS?.map((colName, ind) => (
          <Text
            isTabletext
            key={ind}
            minWidth={widthMapper[ind] || "15%"}
            fontWeight={"medium"}
            ml={ind === 0 ? 2 : 0}
          >
            {colName}
          </Text>
        ))}
      </Flex>

      <ScrollArea height={"100%"}>
        {data?.data?.content?.map((assessment: any, index: number) => {
          return (
            <Box key={assessment?.uuid} mt={0.4}>
              <SimpleAccordion
                onClickHandler={() =>
                  setCurItemExpand((prev) =>
                    prev === assessment?.uuid ? undefined : assessment?.uuid
                  )
                }
                expanded={curItemExpand === assessment?.uuid}
                accordionSummaryContent={
                  <AccordionHead row={assessment} index={index} />
                }
                accordionDetailsContent={
                  <Box overflow={"auto"} maxHeight={"35vh"}>
                    <CustomDataGridV2
                      slots={{
                        columnHeaders: () => null,
                      }}
                      columnHeaderHeight={1}
                      rows={
                        assessment?.responses
                          ? Object.values(assessment?.responses)
                          : []
                      }
                      rowHeight={50}
                      columns={innerColumns}
                      disableRowSelectionOnClick
                      hideFooter
                    />
                  </Box>
                }
              />
            </Box>
          );
        })}

        {!data?.data?.content?.length && (
          <Flex
            width={"100%"}
            height={"100%"}
            alignItems={"center"}
            justifyContent={"center"}
          >
            <Text isTabletext>No Assessment Records Available</Text>
          </Flex>
        )}
      </ScrollArea>
    </Box>
  );
};
